import { FileTextIcon } from 'lucide-react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  DataTable,
  EmptyState,
  ErrorNotice,
  SkeletonTable,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { formatEventDate } from '@/lib/event-time';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';

/** Sortable columns — recap root fields only (event/facilitator are refs). */
const RECAP_SORT_KEYS = ['createdAt'];
import { listRecaps, type EventRecap } from '@/lib/events';

/**
 * Names the recap's facilitator whatever shape V3 returned.
 *
 * @param recap - The recap.
 * @returns A renderable name.
 */
function facilitatorName(recap: EventRecap): string {
  const facilitator = recap.facilitator;
  if (typeof facilitator === 'object' && facilitator !== null) {
    return (
      [facilitator.firstName, facilitator.lastName].filter(Boolean).join(' ') ||
      facilitator.email ||
      'Facilitator'
    );
  }
  return 'Facilitator';
}

/** The facilitator's user id, when V3 populated the ref. */
function facilitatorId(recap: EventRecap): string | undefined {
  const facilitator = recap.facilitator;
  return typeof facilitator === 'object' && facilitator !== null ? facilitator._id : undefined;
}

/**
 * Whether a recap flags anything a human should read.
 *
 * A "No" on a yes/no question is the signal — the standard questionnaire's
 * free-text questions (activities run, suggestions) are always filled in, so
 * a non-empty comment alone means nothing went wrong.
 *
 * @param recap - The recap.
 * @returns True when something needs attention.
 */
function needsAttention(recap: EventRecap): boolean {
  return recap.recap.some(
    (answer) =>
      answer.type === 'yes_no_na' && String(answer.answer ?? '').toLowerCase() === 'no',
  );
}

/**
 * Post-event recaps from facilitators.
 *
 * Sorted to surface the ones that flag issues: the recap that says everything
 * went fine has already done its job by existing.
 *
 * @returns The recaps page.
 */
export function RecapsPage(): React.ReactElement {
  const { search, setSearch, debounced } = useUrlSearch();

  const { sort, onSort } = useTableSort(RECAP_SORT_KEYS);
  const recaps = useQuery({
    queryKey: ['recaps', debounced, sort],
    queryFn: ({ signal }) =>
      listRecaps({ search: debounced, ...(sort ? { sort } : {}) }, signal),
    placeholderData: keepPreviousData,
  });

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="OPERATIONS"
        title="Event recaps"
        subtitle="What facilitators reported after delivery — flags first."
      />

      <div className="mb-4">
        <SearchInput
          label="Search recaps"
          placeholder="Facilitator, client or program"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
      </div>

      {recaps.isError ? (
        <ErrorNotice title="Recaps unavailable" message={errorMessage(recaps.error)} />
      ) : recaps.isPending ? (
        <SkeletonTable columns={["w-[30%]","w-[20%]","w-[14%]","w-[12%]"]} label="Loading recaps…" />
      ) : recaps.data.recaps.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<FileTextIcon aria-hidden className="size-6" />}
            title="No recaps yet"
            message="Facilitators' post-event recaps arrive here after events are delivered."
          />
        </Panel>
      ) : (
        <DataTable
          headers={[
            'Event',
            'Event date',
            'Facilitator',
            { label: 'Submitted', sortKey: 'createdAt' },
            'Status',
            'Recap',
          ]}
          sort={sort}
          onSort={onSort}
          minWidth={840}
        >
          {[...recaps.data.recaps]
            .sort((a, b) => Number(needsAttention(b)) - Number(needsAttention(a)))
            .map((recap) => {
              const event = typeof recap.event === 'object' ? recap.event : null;
              // Contract + company lead — that's how staff identify an event;
              // the program name rides underneath.
              const eventLabel =
                [event?.contractNumber, event?.account?.name].filter(Boolean).join(' · ') ||
                'Event';
              const personId = facilitatorId(recap);
              return (
                <tr key={recap._id} className="border-t border-portal-line">
                  <td className="p-3 text-portal-small">
                    {event?._id ? (
                      <Link
                        to={`/events/${event._id}?tab=recap`}
                        className="text-portal-small font-bold text-brand hover:underline"
                      >
                        {eventLabel}
                      </Link>
                    ) : (
                      <span className="text-portal-small font-bold">{eventLabel}</span>
                    )}
                    <span className="block text-portal-micro text-portal-muted">
                      {event?.programName ?? ''}
                    </span>
                  </td>
                  <td className="p-3 text-portal-small">
                    {formatEventDate(event, 'medium') ?? '—'}
                  </td>
                  <td className="p-3 text-portal-small">
                    {personId ? (
                      <Link
                        to={`/people/${personId}`}
                        className="text-portal-small font-bold text-brand hover:underline"
                      >
                        {facilitatorName(recap)}
                      </Link>
                    ) : (
                      <span className="text-portal-small">{facilitatorName(recap)}</span>
                    )}
                  </td>
                  <td className="p-3 text-portal-small">
                    {recap.createdAt ? new Date(recap.createdAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="p-3 text-portal-small">
                    {needsAttention(recap) ? (
                      <Pill tone="warning">Needs review</Pill>
                    ) : (
                      <Pill tone="success">All clear</Pill>
                    )}
                  </td>
                  <td className="p-3 text-portal-small whitespace-nowrap">
                    <Link
                      to={`/recaps/${recap._id}`}
                      className="font-bold text-brand hover:underline"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              );
            })}
        </DataTable>
      )}
    </section>
  );
}
